import { Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { InsightsViewerCardComponent } from '../../content/cards/insights-viewer-card/insights-viewer-card.component';
import { InsightExtended } from '../../contributions/insight-extended.model';
import { Culture } from '../../cultures';
import { Difference } from '../../differences';
import { EditorialService } from '../editorial.service';
import { InsightTranslationComponent } from '../insight-translation/insight-translation.component';

type CultureOverviewQueryState = {
  culture: string;
  difference: string;
  category: string[] | null;
  type: string[] | null;
  excludeDifferences: string[];
  perPage: number;
  page: number;
};

@Component({
  selector: 'multisite-culture-overviews',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
    InsightsViewerCardComponent,
    InsightTranslationComponent,
  ],
  templateUrl: './culture-overviews.component.html',
  styleUrls: ['./culture-overviews.component.scss'],
})
export class CultureOverviewsComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  private readonly anySelectionValue = '__ANY__';
  private readonly noneSelectionValue = '__NONE__';
  private readonly allowedCategoryOptions: string[] = ['cco-assessment-related'];
  private readonly defaultCategoryStepValue: string[] = ['cco-assessment-related'];
  private readonly allowedTypeOptions: string[] = ['summary'];

  categoryOptions = [
    { label: 'cco-assessment-related', value: 'cco-assessment-related' },
  ];
  typeOptions = [
    { label: 'summary', value: 'summary' },
  ];

  loading = signal<boolean>(false);
  seedLoading = signal<boolean>(true);
  error = signal<string | null>(null);

  cultures = signal<Culture[]>([]);
  differences = signal<Difference[]>([]);
  insights = signal<InsightExtended[]>([]);
  expandedInsightBodies = signal<Record<number, boolean>>({});
  allInsightBodiesExpanded = computed(() => {
    const visibleInsights = this.insights();

    if (!visibleInsights.length) {
      return false;
    }

    const expandedStates = this.expandedInsightBodies();
    return visibleInsights.every((insight) => !!expandedStates[insight.id]);
  });
  resultMeta = signal<any>(null);
  resultLinks = signal<any>(null);

  /**
   * Reveals each insight's translation coverage and the send-for-translation panel. Off by
   * default, matching the card's own default; this route is admin-only, so it is a plain
   * view toggle rather than a permission check.
   */
  showAvailableTranslations = signal<boolean>(false);

  /**
   * The one insight whose translation panel is open, if any. Deliberately single: a run's
   * response carries the full translated text for every combination, so keeping several
   * open would hold a lot of text in memory at once.
   */
  openTranslationPanelInsightId = signal<number | null>(null);

  queryState = signal<CultureOverviewQueryState>({
    culture: '',
    difference: '',
    category: null,
    type: null,
    excludeDifferences: [],
    perPage: 100,
    page: 1,
  });

  form = this.formBuilder.group({
    culture: [''],
    difference: [''],
    category: [[this.anySelectionValue] as string[]],
    type: [[this.anySelectionValue] as string[]],
    excludeDifferences: [''],
    perPage: [100],
    page: [1],
  });

  currentStepLabel = computed(() => {
    const currentState = this.queryState();
    const culture = this.cultures().find((item) => item.slug === currentState.culture);

    if (!culture) {
      return 'No culture selected';
    }

    if (this.isNoneStep(currentState)) {
      return `Culture: ${culture.name} | None of ${this.allDifferenceSlugs().length} differences`;
    }

    const differenceIndex = this.differences().findIndex((item) => item.slug === currentState.difference);
    const difference = this.differences().find((item) => item.slug === currentState.difference);

    if (!difference) {
      return `Culture: ${culture.name} | All insights`;
    }

    const categoryLabel = this.getCategoryStepLabel(currentState.category);

    return `Culture: ${culture.name} | Difference: ${difference.name} (${differenceIndex + 1}/${this.differences().length}) | Category: ${categoryLabel}`;
  });

  constructor(
    private editorialService: EditorialService,
    private route: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
  ) {}

  ngOnInit(): void {
    const seedSubscription = this.editorialService.getReviewSeedData(12).subscribe({
      next: ({ cultures, differences }) => {
        this.cultures.set(cultures ?? []);
        this.differences.set(differences ?? []);
        this.seedLoading.set(false);
        this.startQueryParamSync();
      },
      error: () => {
        this.seedLoading.set(false);
        this.error.set('error.something_went_wrong');
      },
    });

    this.subscriptions.push(seedSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onApplyQueryParams(): void {
    const formValue = this.form.getRawValue();
    const excludeDifferences = this.parseCsv(formValue.excludeDifferences);

    this.navigateToQueryState({
      culture: formValue.culture ?? '',
      difference: formValue.difference ?? '',
      category: this.normalizeFilterSelection(formValue.category, this.allowedCategoryOptions),
      type: this.normalizeFilterSelection(formValue.type, this.allowedTypeOptions),
      excludeDifferences,
      perPage: this.validatePerPage(formValue.perPage),
      page: this.validatePage(formValue.page),
    });
  }

  goToNextView(): void {
    const currentState = this.queryState();
    const cultures = this.cultures();
    const differences = this.differences();

    if (!cultures.length || !differences.length) {
      return;
    }

    const allDifferenceSlugs = differences.map((difference) => difference.slug);
    const cultureIndex = this.getSafeCultureIndex(currentState.culture);
    const differenceIndex = differences.findIndex((difference) => difference.slug === currentState.difference);
    const noneStep = this.isNoneStep(currentState);
    const isCategoryStepOne = this.sameOptionalFilterArrays(currentState.category, this.defaultCategoryStepValue);

    let nextState: CultureOverviewQueryState;

    if (noneStep) {
      const nextCultureIndex = cultureIndex === cultures.length - 1 ? 0 : cultureIndex + 1;
      nextState = {
        ...currentState,
        culture: cultures[nextCultureIndex].slug,
        difference: differences[0].slug,
        category: this.defaultCategoryStepValue,
        type: currentState.type,
        excludeDifferences: [],
        page: 1,
      };
    } else if (differenceIndex >= 0 && isCategoryStepOne) {
      nextState = {
        ...currentState,
        culture: cultures[cultureIndex].slug,
        difference: differences[differenceIndex].slug,
        category: [],
        type: currentState.type,
        excludeDifferences: [],
        page: 1,
      };
    } else if (differenceIndex >= 0 && differenceIndex < differences.length - 1) {
      nextState = {
        ...currentState,
        culture: cultures[cultureIndex].slug,
        difference: differences[differenceIndex + 1].slug,
        category: this.defaultCategoryStepValue,
        type: currentState.type,
        excludeDifferences: [],
        page: 1,
      };
    } else {
      nextState = {
        ...currentState,
        culture: cultures[cultureIndex].slug,
        difference: '',
        category: null,
        type: currentState.type,
        excludeDifferences: allDifferenceSlugs,
        page: 1,
      };
    }

    this.navigateToQueryState(nextState);
  }

  isInsightBodyExpanded(insightId: number): boolean {
    return !!this.expandedInsightBodies()[insightId];
  }

  onInsightBodyExpandedToggle(insightId: number, isExpanded: boolean): void {
    this.expandedInsightBodies.update((currentState) => ({
      ...currentState,
      [insightId]: isExpanded,
    }));
  }

  onToggleAllInsightBodies(): void {
    const visibleInsights = this.insights();

    if (!visibleInsights.length) {
      return;
    }

    const shouldExpandAll = !this.allInsightBodiesExpanded();

    this.expandedInsightBodies.update((currentState) => {
      const nextState = { ...currentState };

      visibleInsights.forEach((insight) => {
        if (shouldExpandAll) {
          nextState[insight.id] = true;
        } else {
          delete nextState[insight.id];
        }
      });

      return nextState;
    });
  }

  onToggleAvailableTranslations(): void {
    const nextValue = !this.showAvailableTranslations();
    this.showAvailableTranslations.set(nextValue);

    if (!nextValue) {
      this.openTranslationPanelInsightId.set(null);
    }
  }

  onSendForTranslationRequest(insightId: number): void {
    this.openTranslationPanelInsightId.update((currentId) => (currentId === insightId ? null : insightId));
  }

  onTranslationPanelClosed(): void {
    this.openTranslationPanelInsightId.set(null);
  }

  /** A run that wrote to the insight changes `translated_locales`, so re-read the page. */
  onTranslationCommitted(): void {
    this.fetchInsights(this.queryState());
  }

  private startQueryParamSync(): void {
    const querySubscription = this.route.queryParamMap.subscribe((queryParamMap) => {
      const parsedState = this.parseAndNormalizeState(queryParamMap);
      const urlState = {
        culture: queryParamMap.get('culture') || '',
        difference: queryParamMap.get('difference') || '',
        category: this.parseFilterFromQueryParam(queryParamMap, 'category', this.allowedCategoryOptions),
        type: this.parseFilterFromQueryParam(queryParamMap, 'type', this.allowedTypeOptions),
        excludeDifferences: this.parseCsv(queryParamMap.get('excludeDifferences') || ''),
        perPage: this.validatePerPage(queryParamMap.get('perPage')),
        page: this.validatePage(queryParamMap.get('page')),
      };

      const urlNeedsNormalization = !this.areStatesEqual(urlState, parsedState);

      if (urlNeedsNormalization) {
        this.navigateToQueryState(parsedState, true);
        return;
      }

      this.queryState.set(parsedState);
      this.patchFormFromState(parsedState);
      this.fetchInsights(parsedState);
    });

    this.subscriptions.push(querySubscription);
  }

  private fetchInsights(queryState: CultureOverviewQueryState): void {
    if (!queryState.culture) {
      this.insights.set([]);
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    const getInsightsSubscription = this.editorialService
      .getInsightsForReview({
        cultures: [queryState.culture],
        differences: queryState.difference ? [queryState.difference] : [],
        category: queryState.category ?? undefined,
        type: queryState.type ?? undefined,
        exclude_differences: queryState.excludeDifferences,
        per_page: queryState.perPage,
        page: queryState.page,
      })
      .subscribe({
        next: (response) => {
          this.insights.set(response?.data ?? []);
          this.resultMeta.set(response?.meta ?? null);
          this.resultLinks.set(response?.links ?? null);
          this.loading.set(false);
        },
        error: (errorMessage) => {
          this.error.set(typeof errorMessage === 'string' ? errorMessage : 'error.something_went_wrong');
          this.insights.set([]);
          this.loading.set(false);
        },
      });

    this.subscriptions.push(getInsightsSubscription);
  }

  private parseAndNormalizeState(queryParamMap: ParamMap): CultureOverviewQueryState {
    const cultures = this.cultures();
    const differences = this.differences();

    const firstCultureSlug = cultures[0]?.slug || '';
    const firstDifferenceSlug = differences[0]?.slug || '';

    const candidateCulture = queryParamMap.get('culture') || firstCultureSlug;
    const cultureExists = cultures.some((culture) => culture.slug === candidateCulture);
    const culture = cultureExists ? candidateCulture : firstCultureSlug;

    const candidateDifference = queryParamMap.get('difference') || '';
    const differenceExists = differences.some((difference) => difference.slug === candidateDifference);
    const difference = differenceExists ? candidateDifference : '';
    const parsedCategory = this.parseFilterFromQueryParam(queryParamMap, 'category', this.allowedCategoryOptions);
    const category = parsedCategory;
    const type = this.parseFilterFromQueryParam(queryParamMap, 'type', this.allowedTypeOptions);

    const parsedExcludeDifferences = this
      .parseCsv(queryParamMap.get('excludeDifferences') || '')
      .filter((slug) => differences.some((difference) => difference.slug === slug));

    const normalizedDifference = !difference && !parsedExcludeDifferences.length ? firstDifferenceSlug : difference;

    return {
      culture,
      difference: normalizedDifference,
      category,
      type,
      excludeDifferences: normalizedDifference ? [] : parsedExcludeDifferences,
      perPage: this.validatePerPage(queryParamMap.get('perPage')),
      page: this.validatePage(queryParamMap.get('page')),
    };
  }

  private patchFormFromState(state: CultureOverviewQueryState): void {
    this.form.patchValue(
      {
        culture: state.culture,
        difference: state.difference,
        category: this.toFormFilterSelection(state.category),
        type: this.toFormFilterSelection(state.type),
        excludeDifferences: state.excludeDifferences.join(','),
        perPage: state.perPage,
        page: state.page,
      },
      { emitEvent: false },
    );
  }

  private navigateToQueryState(state: CultureOverviewQueryState, replaceUrl: boolean = false): void {
    this.router.navigate([], {
      relativeTo: this.route,
      replaceUrl,
      queryParams: {
        culture: state.culture || null,
        difference: state.difference || null,
        category: this.serializeFilterForUrl(state.category),
        type: this.serializeFilterForUrl(state.type),
        excludeDifferences: state.excludeDifferences.length ? state.excludeDifferences.join(',') : null,
        perPage: state.perPage,
        page: state.page,
      },
    });
  }

  private isNoneStep(state: CultureOverviewQueryState): boolean {
    return !state.difference && this.sameStringArrays(state.excludeDifferences, this.allDifferenceSlugs());
  }

  private allDifferenceSlugs(): string[] {
    return this.differences().map((difference) => difference.slug);
  }

  private getSafeCultureIndex(cultureSlug: string): number {
    const index = this.cultures().findIndex((culture) => culture.slug === cultureSlug);
    return index < 0 ? 0 : index;
  }

  private getCategoryStepLabel(category: string[] | null): string {
    if (this.sameOptionalFilterArrays(category, this.defaultCategoryStepValue)) {
      return this.defaultCategoryStepValue[0];
    }

    if (Array.isArray(category) && !category.length) {
      return 'none';
    }

    return 'any';
  }

  private parseCsv(value: string | null | undefined): string[] {
    if (!value) {
      return [];
    }

    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private validatePerPage(value: string | number | null | undefined): number {
    const parsedValue = Number(value);

    if (!Number.isFinite(parsedValue) || parsedValue < 1) {
      return 100;
    }

    return Math.min(parsedValue, 100);
  }

  private validatePage(value: string | number | null | undefined): number {
    const parsedValue = Number(value);

    if (!Number.isFinite(parsedValue) || parsedValue < 1) {
      return 1;
    }

    return parsedValue;
  }

  private normalizeMultiSelectValue(value: string | string[] | null | undefined): string[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value.map((item) => item.trim()).filter(Boolean);
    }

    return this.parseCsv(value);
  }

  private normalizeFilterSelection(
    value: string | string[] | null | undefined,
    allowedValues: string[],
  ): string[] | null {
    const normalizedValues = this.normalizeMultiSelectValue(value);

    if (normalizedValues.includes(this.anySelectionValue)) {
      return null;
    }

    const includesNoneSelection = normalizedValues.includes(this.noneSelectionValue) || normalizedValues.includes('');
    const specificValues = normalizedValues.filter((item) => allowedValues.includes(item));

    if (specificValues.length > 0) {
      return specificValues;
    }

    if (includesNoneSelection) {
      return [];
    }

    return null;
  }

  private parseFilterFromQueryParam(queryParamMap: ParamMap, key: string, allowedValues: string[]): string[] | null {
    if (!queryParamMap.has(key)) {
      return null;
    }

    const rawValues = queryParamMap.getAll(key);
    let hasNoneValue = false;

    const parsedValues = rawValues.reduce((accumulator: string[], rawValue: string) => {
      if (rawValue === '') {
        hasNoneValue = true;
      }

      return accumulator.concat(this.parseCsv(rawValue));
    }, []);

    const filteredValues = parsedValues.filter((value) => allowedValues.includes(value));

    if (filteredValues.length > 0) {
      return filteredValues;
    }

    if (hasNoneValue || rawValues.length > 0) {
      return [];
    }

    return null;
  }

  private toFormFilterSelection(value: string[] | null): string[] {
    if (value === null) {
      return [this.anySelectionValue];
    }

    if (!value.length) {
      return [this.noneSelectionValue];
    }

    return value;
  }

  private serializeFilterForUrl(value: string[] | null): string | null {
    if (value === null) {
      return null;
    }

    if (!value.length) {
      return '';
    }

    return value.join(',');
  }

  private areStatesEqual(a: CultureOverviewQueryState, b: CultureOverviewQueryState): boolean {
    return (
      a.culture === b.culture &&
      a.difference === b.difference &&
      this.sameOptionalFilterArrays(a.category, b.category) &&
      this.sameOptionalFilterArrays(a.type, b.type) &&
      a.perPage === b.perPage &&
      a.page === b.page &&
      this.sameStringArrays(a.excludeDifferences, b.excludeDifferences)
    );
  }

  private sameOptionalFilterArrays(a: string[] | null, b: string[] | null): boolean {
    if (a === null || b === null) {
      return a === b;
    }

    return this.sameStringArrays(a, b);
  }

  private sameStringArrays(a: string[], b: string[]): boolean {
    const arrayA = [...a].sort();
    const arrayB = [...b].sort();

    if (arrayA.length !== arrayB.length) {
      return false;
    }

    for (let index = 0; index < arrayA.length; index += 1) {
      if (arrayA[index] !== arrayB[index]) {
        return false;
      }
    }

    return true;
  }
}
